import React, { useEffect, useMemo, useState } from "react";
import {
  Permission,
  Person,
  HydratedOpportunity,
  OpportunityStatistics as OpportunityStatisticsType,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
  GetOpportunityStatisticsResponseSchema,
  ListOpportunitiesResponseSchema,
  UpdatePersonData,
  UpdateOpportunityParams,
} from "@forgepoint/shared";
import { Loader } from "lucide-react";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePeopleStore } from "./peopleStore";
import { useCanEditOwned } from "@/store/authStore";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { OpportunityStatistics } from "@/modules/opportunities/OpportunityStatistics";
import { OpportunityDetailSheet } from "@/modules/opportunities/OpportunityDetailSheet";
import { NotesSection } from "@/components/notes/NotesSection";
import { useNotesStore } from "@/store/notesStore";
import { CompanySelector } from "@/components/CompanySelector";

/**
 * A settings-row style form field with label on the left and control on the right.
 * @param props - Label text and the control rendered on the right
 * @returns Horizontal form row element
 */
function FormRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-4">
        <Label
          htmlFor={htmlFor}
          className="text-sm font-medium leading-tight shrink-0 text-muted-foreground"
        >
          {label}
        </Label>
        <div className="w-[320px] shrink-0">{children}</div>
      </div>
    </div>
  );
}

/**
 * Props for the PersonDetailSheet component.
 */
interface PersonDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
}

/**
 * Local mirror of the person's editable attributes. Edits are buffered here
 * until the user saves.
 */
interface PersonFormData {
  firstName: string;
  lastName: string;
  email: string;
  companyId: string | null;
  isPrimaryCompanyContact: boolean;
}

/**
 * Builds form data from a person.
 * @param person - The person to extract data from
 * @returns Form data matching the person's current values
 */
const getFormDataFromPerson = (person: Person): PersonFormData => ({
  firstName: person.firstName,
  lastName: person.lastName,
  email: person.email ?? "",
  companyId: person.companyId ?? null,
  isPrimaryCompanyContact: person.isPrimaryCompanyContact,
});

/**
 * Formats a currency amount string for display.
 * @param amount - The amount string to format
 * @returns Formatted currency string
 */
const formatCurrency = (amount: string): string => {
  const num = parseFloat(amount);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * A sheet component that displays person details with inline-editable fields.
 * Edits are buffered locally and persisted together when the user clicks
 * Save Changes in the sheet footer.
 * Organizes associated data (deals, colleagues, notes) in tabs and supports
 * stacked sheets for viewing/editing associated opportunities and colleagues.
 * @param props - Sheet control props and the person ID to display
 * @returns The PersonDetailSheet component
 */
export default function PersonDetailSheet({
  open,
  onOpenChange,
  personId,
}: PersonDetailSheetProps) {
  const {
    people,
    personLoadingStates,
    fetchPerson,
    updatePerson,
    fetchPeople,
  } = usePeopleStore();
  const { companies, fetchCompany } = useCompaniesStore();
  const { updateOpportunity } = useOpportunitiesStore();
  const fetchNotes = useNotesStore((s) => s.fetchNotes);

  const person = people.get(personId);
  // People are owned by their company's sales rep, so edit.own scope derives
  // from the persisted company (not the buffered form selection).
  const personCompany = person?.companyId
    ? companies.get(person.companyId)
    : undefined;
  const canEdit = useCanEditOwned(
    Permission.PEOPLE_EDIT_ALL,
    Permission.PEOPLE_EDIT_OWN,
    personCompany?.salesRepUserId,
  );
  const loadingState = personLoadingStates.get(personId);
  const isPersonLoading = loadingState?.isLoadingGet ?? false;
  const personError = loadingState?.getError ?? null;

  const [formData, setFormData] = useState<PersonFormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [statistics, setStatistics] =
    useState<OpportunityStatisticsType | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [deals, setDeals] = useState<HydratedOpportunity[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  const [editingOpportunityId, setEditingOpportunityId] = useState<
    string | null
  >(null);
  const [viewingColleague, setViewingColleague] = useState<Person | null>(null);

  const isSynced = Boolean(person && person.externalMappings.length > 0);
  // Fields render read-only when the person is ERP-synced or the user lacks edit scope.
  const isReadOnly = isSynced || !canEdit;

  const linkedCompany = formData?.companyId
    ? companies.get(formData.companyId)
    : undefined;
  const companyName = linkedCompany?.name ?? null;

  const colleagues = useMemo(() => {
    if (!person?.companyId) {
      return [];
    }
    const result: Person[] = [];
    people.forEach((p) => {
      if (p.companyId === person.companyId && p.id !== personId) {
        result.push(p);
      }
    });
    return result;
  }, [people, person?.companyId, personId]);

  // Fetch person data when sheet opens
  useEffect(() => {
    if (!open) {
      return;
    }
    fetchPerson(personId);
    fetchNotes("PERSON", personId);
  }, [open, personId, fetchPerson, fetchNotes]);

  // Initialize form data when person loads
  useEffect(() => {
    if (!open || !person) {
      return;
    }
    setFormData(getFormDataFromPerson(person));
  }, [open, person?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch company + colleagues when person's companyId is known
  useEffect(() => {
    if (!open || !person?.companyId) {
      return;
    }
    fetchCompany(person.companyId);
    fetchPeople({ companyId: person.companyId });
  }, [open, person?.companyId, fetchCompany, fetchPeople]);

  // Fetch stats + deals for the person's own opportunity associations
  useEffect(() => {
    if (!open) {
      return;
    }
    fetchPersonStatistics();
    fetchPersonDeals();
  }, [open, personId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Fetches opportunity statistics scoped to this person's associations.
   */
  const fetchPersonStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const response = await fetchApi(
        `opportunities/statistics?personId=${personId}`,
        "GET",
      );
      if (response.ok && response.data) {
        const data = GetOpportunityStatisticsResponseSchema.parse(
          response.data,
        );
        setStatistics(data.statistics);
      }
    } catch (error) {
      captureException(error as Error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  /**
   * Fetches opportunities this person is associated with.
   */
  const fetchPersonDeals = async () => {
    setDealsLoading(true);
    try {
      const response = await fetchApi(
        `opportunities?personId=${personId}`,
        "GET",
      );
      if (response.ok && response.data) {
        const data = ListOpportunitiesResponseSchema.parse(response.data);
        setDeals(data.opportunities);
      }
    } catch (error) {
      captureException(error as Error);
    } finally {
      setDealsLoading(false);
    }
  };

  /**
   * Handles sheet open/close, resetting transient state when closed.
   * @param newOpen - Whether the sheet should be open
   */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData(null);
      setEditingOpportunityId(null);
      setViewingColleague(null);
    }
    onOpenChange(newOpen);
  };

  /**
   * Buffers a company change, clearing the primary-contact flag when no company
   * is selected (persisted on Save).
   * @param companyId - The selected company ID, or null to clear
   */
  const handleCompanySelect = (companyId: string | null) => {
    const isPrimaryCompanyContact = companyId
      ? (formData?.isPrimaryCompanyContact ?? false)
      : false;
    setFormData((prev) =>
      prev ? { ...prev, companyId, isPrimaryCompanyContact } : prev,
    );
  };

  /**
   * Buffers a primary-contact change. No-op when no company is selected.
   * @param checked - Whether the person is the primary company contact
   */
  const handlePrimaryToggle = (checked: boolean) => {
    if (!formData?.companyId) {
      return;
    }
    setFormData((prev) =>
      prev ? { ...prev, isPrimaryCompanyContact: checked } : prev,
    );
  };

  /**
   * Persists all pending person edits, rejecting empty required name fields.
   */
  const handleSave = async () => {
    if (!person || !formData) {
      return;
    }
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    if (!firstName) {
      toast.error("First name is required");
      return;
    }
    if (!lastName) {
      toast.error("Last name is required");
      return;
    }

    setIsSaving(true);
    try {
      const updateData: UpdatePersonData = {
        firstName,
        lastName,
        email: formData.email.trim(),
        companyId: formData.companyId,
        isPrimaryCompanyContact: formData.isPrimaryCompanyContact,
      };
      const result = await updatePerson(personId, updateData);
      if (!result) {
        toast.error("Failed to update person");
        return;
      }
      setFormData((prev) =>
        prev
          ? { ...prev, firstName, lastName, email: updateData.email ?? "" }
          : prev,
      );
      toast.success("Person updated successfully");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handles saving an edited opportunity and refreshes deals/stats.
   * @param data - The opportunity update payload
   * @returns True on success, or an error message string
   */
  const handleOpportunitySave = async (
    data: UpdateOpportunityParams,
  ): Promise<true | string> => {
    if (!editingOpportunityId) {
      return "No opportunity selected";
    }
    const { error } = await updateOpportunity(editingOpportunityId, data);
    if (error) {
      return error;
    }
    fetchPersonDeals();
    fetchPersonStatistics();
    return true;
  };

  const colleaguesColumns: DataTableColumn<Person>[] = [
    {
      key: "name",
      header: "Name",
      type: "string",
      format: (_value: unknown, p: Person) =>
        [p.firstName, p.lastName].filter(Boolean).join(" ") || "Unknown",
    },
    {
      key: "email",
      header: "Email",
      type: "string",
      format: (_value: unknown, p: Person) => p.email || "-",
    },
    {
      key: "isPrimaryCompanyContact",
      header: "Role",
      type: "string",
      width: 120,
      format: (_value: unknown, p: Person) =>
        p.isPrimaryCompanyContact ? (
          <Badge variant="secondary">Primary</Badge>
        ) : null,
    },
  ];

  const dealsColumns: DataTableColumn<HydratedOpportunity>[] = [
    { key: "name", header: "Name", type: "string" },
    {
      key: "amount",
      header: "Amount",
      type: "string",
      width: 140,
      format: (_value: unknown, deal: HydratedOpportunity) =>
        formatCurrency(deal.amount),
    },
    {
      key: "status",
      header: "Status",
      type: "string",
      width: 160,
      format: (_value: unknown, deal: HydratedOpportunity) => (
        <Badge variant="secondary">
          {OPPORTUNITY_STATUS_DISPLAY_NAMES[deal.status]}
        </Badge>
      ),
    },
    { key: "createdAt", header: "Created", type: "date", width: 120 },
  ];

  const fullName = formData
    ? [formData.firstName, formData.lastName].filter(Boolean).join(" ")
    : "";

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-1/2 max-w-none flex flex-col p-0">
          <div className="flex-1 overflow-y-auto px-6">
            {personError && !person ? (
              <div className="flex h-full w-full items-center justify-center p-6">
                <div className="text-center text-destructive">
                  <p>Failed to load person: {personError}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => fetchPerson(personId)}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : isPersonLoading && !person ? (
              <div className="flex h-full w-full items-center justify-center p-6">
                <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : person && formData ? (
              <div className="py-6 flex flex-col gap-6">
                {/* Header: monogram + name */}
                <div className="flex items-center gap-3.5">
                  <div
                    aria-hidden="true"
                    className="flex-none w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
                  >
                    <span className="text-sm font-semibold text-primary/70">
                      {(formData.firstName || formData.lastName || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-2.5">
                    <h1 className="text-2xl font-bold truncate">
                      {fullName || "Unnamed"}
                    </h1>
                    {isSynced && (
                      <Badge
                        variant="outline"
                        className="text-[10px] font-normal tracking-wider uppercase px-1.5 py-0 border-muted-foreground/25"
                      >
                        Synced
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Details form — edits buffered locally, saved via the Save button */}
                <div className="divide-y">
                  <FormRow label="First Name" htmlFor="person-first-name">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.firstName || "-"}</p>
                    ) : (
                      <Input
                        id="person-first-name"
                        value={formData.firstName}
                        placeholder="First name"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev
                              ? { ...prev, firstName: e.target.value }
                              : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Last Name" htmlFor="person-last-name">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.lastName || "-"}</p>
                    ) : (
                      <Input
                        id="person-last-name"
                        value={formData.lastName}
                        placeholder="Last name"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev ? { ...prev, lastName: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Email" htmlFor="person-email">
                    {isReadOnly ? (
                      <p className="text-sm">{formData.email || "-"}</p>
                    ) : (
                      <Input
                        id="person-email"
                        type="email"
                        value={formData.email}
                        placeholder="Email address"
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev ? { ...prev, email: e.target.value } : prev,
                          )
                        }
                      />
                    )}
                  </FormRow>

                  <FormRow label="Company">
                    {isReadOnly ? (
                      <p className="text-sm">{companyName ?? "-"}</p>
                    ) : (
                      <CompanySelector
                        currentlySelectedId={formData.companyId}
                        onSelectId={handleCompanySelect}
                        placeholder="Select a company"
                      />
                    )}
                  </FormRow>

                  {formData.companyId && (
                    <FormRow
                      label="Primary Contact"
                      htmlFor="person-primary-contact"
                    >
                      {isReadOnly ? (
                        <p className="text-sm">
                          {formData.isPrimaryCompanyContact ? "Yes" : "No"}
                        </p>
                      ) : (
                        <div className="flex h-9 items-center">
                          <Switch
                            id="person-primary-contact"
                            checked={formData.isPrimaryCompanyContact}
                            onCheckedChange={handlePrimaryToggle}
                          />
                        </div>
                      )}
                    </FormRow>
                  )}
                </div>

                {/* Stats band */}
                <div className="border-b py-4">
                  <OpportunityStatistics
                    statistics={statistics}
                    isLoading={statisticsLoading}
                    hideWinRate
                  />
                </div>

                {/* Associated data tabs */}
                <Tabs defaultValue="deals" className="pt-1">
                  <TabsList
                    className="grid h-8 w-full"
                    style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
                  >
                    <TabsTrigger value="deals" className="px-2 text-xs">
                      Deals
                      <span className="ml-1 tabular-nums">
                        ({deals.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="colleagues" className="px-2 text-xs">
                      Colleagues
                      <span className="ml-1 tabular-nums">
                        ({colleagues.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="px-2 text-xs">
                      Notes
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="deals" className="mt-4 space-y-5">
                    <DataTable
                      columns={dealsColumns}
                      data={deals}
                      isLoading={dealsLoading && deals.length === 0}
                      hideSearch
                      onClickRow={(deal) => setEditingOpportunityId(deal.id)}
                    />
                  </TabsContent>

                  <TabsContent value="colleagues" className="mt-4 space-y-5">
                    <DataTable
                      columns={colleaguesColumns}
                      data={colleagues}
                      hideSearch
                      onClickRow={(p) => setViewingColleague(p)}
                    />
                  </TabsContent>

                  <TabsContent value="notes" className="mt-4 space-y-5">
                    <NotesSection entityType="PERSON" entityId={personId} />
                  </TabsContent>
                </Tabs>
              </div>
            ) : null}
          </div>
          {person && formData && !isReadOnly && (
            <div className="flex justify-end gap-2 border-t p-4">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Stacked opportunity detail sheet */}
      {editingOpportunityId && (
        <OpportunityDetailSheet
          open={!!editingOpportunityId}
          onOpenChange={(newOpen: boolean) =>
            !newOpen && setEditingOpportunityId(null)
          }
          opportunityId={editingOpportunityId}
          onSave={handleOpportunitySave}
          mode="edit"
        />
      )}

      {/* Stacked colleague detail sheet */}
      {viewingColleague && (
        <PersonDetailSheet
          open={!!viewingColleague}
          onOpenChange={(newOpen) => {
            if (!newOpen) {
              setViewingColleague(null);
            }
          }}
          personId={viewingColleague.id}
        />
      )}
    </>
  );
}
